import { useId } from 'react'
import './mytiledesk-mark.css'

// The myTileDesk mark: a self-lit icon chip, not line art.
//
// TileDeskMark.jsx draws straight onto the page ground, which is why it has
// to be "lifted" for a dark theme — see the brand section of tokens.css. This
// mark carries its own fixed cream backdrop instead, the way a real app icon
// is a fixed badge regardless of the surface it sits on, so nothing here
// needs a theme redefinition except the wordmark's own "tile", which sits on
// the page ground as real text rather than inside the chip.
//
// The glyph is ONE real bold letterform — the actual outline of a block "M",
// traced as a single closed path — split into two colours by a straight
// vertical clip through the middle, not built from separate pieces:
//
//   attempt 1: a thin M-shaped stroke in a rounded square. That silhouette IS
//   the envelope glyph (✉) and read as one on sight.
//   attempt 2: solid legs plus a separate fold triangle that did not share a
//   top edge with them, leaving a hole in the glyph.
//   attempt 3, this one: rendered at 160px, 28px and 16px and looked at
//   directly (a local headless-browser render, not a guess) before being
//   kept. Two-tone-by-clip rather than two-tone-by-piece means the shape
//   itself is guaranteed correct — it is one letterform, verified — and the
//   colour split cannot introduce a seam or a gap the way stitching separate
//   polygons together did twice before.
const M_PATH =
  'M3 20 L3 4 L7.5 4 L12 12.5 L16.5 4 L21 4 L21 20 L16.5 20 L16.5 9.5 L12 17.5 L7.5 9.5 L7.5 20 Z'

/** The icon chip. 20px and up — the top bar, the loading splash. */
export function MyTileDeskMark({ size = 28, title = 'MyTileDesk' }) {
  // Unique per instance: two copies of this mark on one page (a route that
  // somehow rendered the splash and the top bar at once, say) would otherwise
  // share clipPath ids, and the second instance's clip references the first
  // instance's geometry rather than its own.
  const uid = useId()
  const leftClip = `${uid}-mtd-left`
  const rightClip = `${uid}-mtd-right`
  return (
    <svg className="mtd" width={size} height={size} viewBox="0 0 24 24" role="img" aria-label={title}>
      <rect className="mtd-chip" x="0.5" y="0.5" width="23" height="23" rx="5" />
      <clipPath id={leftClip}><rect x="0" y="0" width="12" height="24" /></clipPath>
      <clipPath id={rightClip}><rect x="12" y="0" width="12" height="24" /></clipPath>
      <path className="mtd-half mtd-half-a" d={M_PATH} clipPath={`url(#${leftClip})`} />
      <path className="mtd-half mtd-half-b" d={M_PATH} clipPath={`url(#${rightClip})`} />
    </svg>
  )
}

/**
 * The wordmark. "my" is quiet — the personal, small-shop word. "tile" carries
 * the brand accent, because it is the one word in the name that says what
 * this is for. "desk" is bold — the promise, the part that is yours. Same
 * weak/strong shape as the TileDesk wordmark it replaces in the top bar, with
 * a third, coloured beat added in the middle.
 */
export function MyTileDeskWordmark({ size = 'md' }) {
  return (
    <span className={`mtd-wm mtd-wm-${size}`} role="img" aria-label="MyTileDesk">
      <span className="mtd-wm-my" aria-hidden="true">my</span>
      <span className="mtd-wm-tile" aria-hidden="true">tile</span>
      <span className="mtd-wm-desk" aria-hidden="true">desk</span>
    </span>
  )
}

/** Mark plus wordmark, for the top bar and the loading splash. */
export function MyTileDeskLockup({ markSize = 28, size = 'sm' }) {
  return (
    <span className="mtd-lockup">
      <MyTileDeskMark size={markSize} />
      <MyTileDeskWordmark size={size} />
    </span>
  )
}
